import { useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2, ImagePlus, User, Star } from "lucide-react";

const shapeClass = (isAvatar) => (isAvatar ? "w-20 h-20 rounded-full" : "w-24 h-24 rounded-lg");

async function uploadOne(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

function Thumbnail({ url, index, isAvatar, showFeatured, testid, onRemove, onFeature }) {
  const isFeatured = showFeatured && index === 0;
  return (
    <div className={`relative group ${shapeClass(isAvatar)} overflow-hidden border ${isFeatured ? "border-primary ring-2 ring-primary/30" : "border-border"} bg-secondary`}>
      <img src={fileUrl(url)} alt="upload" className="w-full h-full object-cover" />
      <button type="button" data-testid={`${testid}-remove-${index}`} onClick={() => onRemove(index)}
        className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <X className="w-3.5 h-3.5" />
      </button>
      {isFeatured && (
        <span data-testid={`${testid}-featured-badge`} className="absolute bottom-0 inset-x-0 bg-primary/90 text-white text-[9px] font-bold uppercase tracking-wide py-0.5 flex items-center justify-center gap-0.5">
          <Star className="w-2.5 h-2.5 fill-white" /> Featured
        </span>
      )}
      {showFeatured && index !== 0 && (
        <button type="button" title="Set as featured" data-testid={`${testid}-feature-${index}`} onClick={() => onFeature(index)}
          className="absolute bottom-0.5 left-0.5 bg-black/60 hover:bg-primary text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Star className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function AddButton({ isAvatar, busy, testid, onClick }) {
  let icon = <ImagePlus className="w-6 h-6" />;
  if (busy) icon = <Loader2 className="w-5 h-5 animate-spin" />;
  else if (isAvatar) icon = <User className="w-6 h-6" />;
  return (
    <button type="button" data-testid={`${testid}-add`} disabled={busy} onClick={onClick}
      className={`${shapeClass(isAvatar)} border-2 border-dashed border-border grid place-items-center text-muted-foreground hover:border-primary hover:text-primary transition-colors`}>
      {icon}
    </button>
  );
}

// Reusable image uploader. `multiple` toggles avatar (single url) vs gallery (array of urls).
// When `featured` is set, the first image is treated as the cover and can be re-picked.
export function ImageUploader({ value, onChange, multiple = false, shape = "rect", testid = "image-uploader", label, maxImages = 10, featured = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const isAvatar = shape === "avatar";
  const showFeatured = multiple && featured;
  const urls = multiple ? (value || []) : (value ? [value] : []);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    try {
      if (!multiple) {
        onChange(await uploadOne(list[0]));
      } else {
        const room = Math.max(0, maxImages - (value || []).length);
        if (room <= 0) { toast.error(`You can upload up to ${maxImages} photos`); return; }
        const uploaded = [];
        for (const f of list.slice(0, room)) uploaded.push(await uploadOne(f));
        onChange([...(value || []), ...uploaded]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (i) => onChange(multiple ? (value || []).filter((_, idx) => idx !== i) : null);
  const setFeatured = (i) => { if (multiple && i !== 0) onChange([value[i], ...value.filter((_, idx) => idx !== i)]); };
  const showAddButton = multiple || urls.length === 0;

  return (
    <div data-testid={testid}>
      {label && <div className="text-sm font-medium mb-2">{label}</div>}
      {showFeatured && urls.length > 0 && (
        <p className="text-xs text-muted-foreground mb-2">The <span className="font-semibold text-foreground">featured</span> photo (★) is shown first to tenants. Hover a photo and tap the star to feature it.</p>
      )}
      <div className={`flex flex-wrap gap-3 ${isAvatar ? "items-center" : ""}`}>
        {urls.map((u, i) => (
          <Thumbnail key={`${i}_${u}`} url={u} index={i} isAvatar={isAvatar} showFeatured={showFeatured}
            testid={testid} onRemove={removeAt} onFeature={setFeatured} />
        ))}
        {showAddButton && <AddButton isAvatar={isAvatar} busy={busy} testid={testid} onClick={() => inputRef.current?.click()} />}
      </div>
      {!isAvatar && (
        <button type="button" disabled={busy} onClick={() => inputRef.current?.click()}
          className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
          <Upload className="w-3.5 h-3.5" /> {multiple ? "Add photos" : "Upload photo"}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" multiple={multiple} className="hidden"
        data-testid={`${testid}-input`} onChange={(e) => handleFiles(e.target.files)} />
    </div>
  );
}
